/**
 * AppProfilePopover
 * =================
 * Presentational shell for the account menu, shared by the platform and
 * admin/spend navbars. It renders the DS `Menu /Profile` assembly
 * (a local ProfileHeader row + grouped DS MenuItem rows) and owns none of the
 * product-specific behaviour: routing, drawers, role/entitlement gating,
 * and the version/legal footer are supplied by the consumer.
 *
 * Figma source of truth
 * ---------------------
 *   File: Design System — ybmTCju67bW6J3qBaa1gcF
 *   Node: 5795:104 (Menu / Profile)
 *
 * Composition
 * -----------
 *   - `name` / `company` / `avatarSrc` drive the ProfileHeader, which is
 *     domain composition and so lives here rather than in the DS.
 *   - `sections` is an array of groups; each group is a list of menu
 *     items, and groups are separated by a Divider. Falsy items are
 *     filtered out so callers can inline conditionals (e.g. an
 *     entitlement-gated row) without juggling arrays.
 *   - `footer` is an optional node rendered below a final Divider —
 *     used for the app version and Privacy / Terms links.
 *
 * Each row's leading slot is a small outlined Avatar wrapping the item's
 * Hugeicon, matching the design. Set `external` to add a trailing
 * link-out glyph for rows that navigate away from the current view.
 *
 * @example
 * <AppProfilePopover
 *   open={open}
 *   anchorEl={anchorEl}
 *   onClose={close}
 *   name="Katerina Shultz"
 *   company="Clark Construction"
 *   sections={[
 *     [{ icon: UserCircle02Icon, label: "Profile", onClick: openProfile }],
 *     [{ icon: Logout01Icon, label: "Logout", onClick: logout }],
 *   ]}
 *   footer={<VersionAndLegal />}
 * />
 */
import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { LinkSquare01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Avatar, Menu, MenuItem, menuDividerSx } from "@outpave/ds";

// Hugeicons icon components are passed by reference (not rendered) so
// the shell controls sizing. Derive the type from a known icon.
type HugeIcon = typeof LinkSquare01Icon;

const identityLineSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

/**
 * The account-identity row at the top of the menu: an avatar beside the name
 * and company. Domain composition, so the DS does not own it. It renders as a
 * presentational `li` because MUI's Menu manages focus over its own children.
 */
const ProfileHeader: React.FC<{
  name?: string;
  company?: string;
  avatarSrc?: string | null;
}> = ({ name, company, avatarSrc }) => (
  <Box
    component="li"
    role="presentation"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "var(--space-4)",
      padding: "var(--space-3)",
      minWidth: 0,
      listStyle: "none",
    }}
  >
    <Avatar size="lg" src={avatarSrc ?? undefined} name={name} alt={name} />
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        gap: "var(--space-2)",
      }}
    >
      {name && (
        <Typography variant="subtitle1" sx={identityLineSx}>
          {name}
        </Typography>
      )}
      {company && (
        <Typography
          variant="label"
          sx={{ ...identityLineSx, color: "var(--color-text-tertiary)" }}
        >
          {company}
        </Typography>
      )}
    </Box>
  </Box>
);

export interface AppProfileMenuItem {
  /** Hugeicons icon component, rendered inside the leading avatar. */
  icon: HugeIcon;
  /** Row label. */
  label: React.ReactNode;
  /** Activation handler — usually navigate + close. */
  onClick?: () => void;
  /** Adds a trailing link-out glyph for rows that leave the current view. */
  external?: boolean;
  /** Non-interactive, dimmed. */
  disabled?: boolean;
  /** Hide the row from the menu. */
  hidden?: boolean;
}

/** A menu group, or a block of controls that is not a menu row (the preferences). */
export type AppProfileMenuSection =
  | Array<AppProfileMenuItem | null | false | undefined>
  | { content: React.ReactNode };

export interface AppProfilePopoverProps {
  /** Popover id, forwarded to the underlying Menu for aria wiring. */
  id?: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Identity header — person's name. */
  name?: string;
  /** Identity header — company / org line. */
  company?: string;
  /** Identity header — avatar image; falls back to initials from `name`. */
  avatarSrc?: string | null;
  /**
   * Menu groups. Each inner array is a section; sections are separated by
   * a Divider. Falsy entries are dropped, so callers can inline
   * conditional rows.
   */
  sections: AppProfileMenuSection[];
  /** Optional footer node (app version, legal links). */
  footer?: React.ReactNode;
}

const AppProfilePopover: React.FC<AppProfilePopoverProps> = ({
  id,
  open,
  anchorEl,
  onClose,
  name,
  company,
  avatarSrc,
  sections,
  footer,
}) => {
  // Resolve each group to its *visible* items (drop falsy + hidden), then
  // drop any group left empty. Doing this before the divider logic below
  // is what prevents two dividers wrapping a section whose only item is
  // hidden — the empty section no longer exists as a group.
  const groups = sections
    .map((group) =>
      Array.isArray(group)
        ? (group.filter(Boolean) as AppProfileMenuItem[]).filter(
            (item) => !item.hidden,
          )
        : group,
    )
    .filter((group) => !Array.isArray(group) || group.length > 0);

  // MUI's Menu doesn't accept Fragment children (it can't manage their
  // focus), so flatten everything into a single keyed array.
  const children: React.ReactNode[] = [
    <ProfileHeader
      key="header"
      name={name}
      company={company}
      avatarSrc={avatarSrc}
    />,
    <Divider
      key="header-divider"
      component="li"
      role="separator"
      sx={menuDividerSx}
    />,
  ];

  groups.forEach((group, gi) => {
    if (!Array.isArray(group)) {
      children.push(
        <li
          key={`c${gi}`}
          role="presentation"
          tabIndex={-1}
          style={{ listStyle: "none" }}
          // Keys typed into these controls are theirs, not the menu's type-ahead; Escape still closes it.
          onKeyDown={(event) => {
            if (event.key !== "Escape") event.stopPropagation();
          }}
        >
          {group.content}
        </li>,
      );
    }
    (Array.isArray(group) ? group : []).forEach((item, ii) => {
      children.push(
        <MenuItem
          key={`g${gi}-i${ii}`}
          onClick={item.onClick}
          disabled={item.disabled}
          leading={
            <Avatar
              size="sm"
              icon={<HugeiconsIcon icon={item.icon} size={12} />}
            />
          }
          trailing={
            item.external ? (
              <HugeiconsIcon icon={LinkSquare01Icon} size={14} />
            ) : undefined
          }
        >
          {item.label}
        </MenuItem>,
      );
    });
    if (gi < groups.length - 1) {
      children.push(
        <Divider
          key={`d${gi}`}
          component="li"
          role="separator"
          sx={menuDividerSx}
        />,
      );
    }
  });

  if (footer) {
    children.push(
      <Divider
        key="footer-divider"
        component="li"
        role="separator"
        sx={menuDividerSx}
      />,
      <li
        key="footer"
        role="presentation"
        tabIndex={-1}
        style={{ listStyle: "none" }}
      >
        {footer}
      </li>,
    );
  }

  return (
    <Menu
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{ paper: { sx: { width: 280 } } }}
    >
      {children}
    </Menu>
  );
};

export default AppProfilePopover;
